import { useMemo } from 'react'

import {
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { CONVERSATION_FACTS_DIMENSIONS } from 'cube/constants/QueryConstants'
import { MedianConversationLengthBaseQuery } from 'cube/queries/MedianConversationLength.query'
import { isNil } from 'ramda'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { resolveCubeKPIResults } from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

export interface MedianConversationLengthResult {
  metric?: number | null
  previousMetric?: number | null
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
}

/***
 * Hook that uses cube to fetch conversation length data for the current and previous time period.
 * Median conversation length is defined as the median duration between the first and last enduser interaction in seconds.
 *
 * @returns {MedianConversationLengthResult}
 */
export const useMedianConversationLength =
  (): MedianConversationLengthResult => {
    const timePeriod = useTimePeriod()
    const { pageFilters } = useCubeReportPageFiltersContext()
    const { accessFilters } = useCubeReportAccessFiltersContext()

    const cubeQueryCurrent = new CubeQueryBuilder({
      query: MedianConversationLengthBaseQuery,
    })
      .withFilters([...pageFilters, ...accessFilters])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      )
      .build()

    const cubeQueryPrevious = new CubeQueryBuilder({
      query: MedianConversationLengthBaseQuery,
    })
      .withFilters([...pageFilters, ...accessFilters])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.prevStart.toDate(),
        timePeriod.prevEnd.toDate()
      )
      .build()

    const {
      data: resultSetCurrent,
      isLoading: isLoadingCurrent,
      isError: isErrorCurrent,
    } = useCubeQuery({
      query: cubeQueryCurrent,
    })

    const { data: resultSetPrevious, isLoading: isLoadingPrevious } =
      useCubeQuery({
        query: cubeQueryPrevious,
      })

    const medianConversationLengthCurrent = useMemo(
      () =>
        resolveCubeKPIResults(
          resultSetCurrent,
          MedianConversationLengthBaseQuery.queryName ?? ''
        ),
      [resultSetCurrent]
    )

    const medianConversationLengthPrevious = useMemo(
      () =>
        resolveCubeKPIResults(
          resultSetPrevious,
          MedianConversationLengthBaseQuery.queryName ?? ''
        ),
      [resultSetPrevious]
    )

    const metric = useMemo(
      () => medianConversationLengthCurrent ?? null,
      [medianConversationLengthCurrent]
    )

    const previousMetric = useMemo(
      () => medianConversationLengthPrevious ?? null,
      [medianConversationLengthPrevious]
    )

    const isLoading = useMemo(
      () => isLoadingCurrent || isLoadingPrevious,
      [isLoadingCurrent, isLoadingPrevious]
    )

    const hasNoData = useMemo(
      () => !isLoading && isNil(medianConversationLengthCurrent),
      [isLoading, medianConversationLengthCurrent]
    )

    const hasError = useMemo(
      () => !isLoading && isErrorCurrent,
      [isLoading, isErrorCurrent]
    )

    return useMemo(
      () => ({ metric, previousMetric, isLoading, hasNoData, hasError }),
      [metric, previousMetric, isLoading, hasNoData, hasError]
    )
  }

export default useMedianConversationLength
